export interface ArticleStyleVariants {
  wrapper?: string
  image?: string
  headline?: string
  playButton?: string
  publisher?: {
    wrapper?: string
    text?: string
    icon?: string | boolean
    ad?: string | boolean
  }
  subtitle?: string
  button?: string
}

export function getArticleStyleVariants(
  props: ArticleCardProps
): ArticleStyleVariants {
  // No initialization needed as we are not using the results object

  // TITLE STYLES - VIDEO-S
  if (props.design === 'msn' && props.variant === 'video-s') {
    return {
      wrapper:
        'w-[200px] h-[246px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-[6px] hover:shadow-md',
      image:
        'w-[200px] h-[126px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-16px',
      headline:
        'w-[200px] h-[60px] text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[14px] order-3 px-4 !leading-[20px]',
      playButton: 'h-5 translate-y-[-25px]',
      publisher: {
        wrapper:
          'w-[200px] h-[16px] flex items-center gap-1 order-2 px-4 mb-[8px]',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // TITLE STYLES - VIDEO-L
  if (props.design === 'msn' && props.variant === 'video-l') {
    return {
      wrapper:
        'w-[437px] h-[246px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer rounded-[6px] hover:shadow-md gap-[10px]',
      playButton: 'h-5 translate-y-[23px]',
      image:
        'w-[437px] h-[246px] rounded-lg object-cover transition duration-300 group-hover:brightness-[0.8]',
    }
  }

  if (props.variant === '2x2' && props.design === 'msn') {
    return {
      wrapper:
        'h-[620px] w-[612px] flex-col group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'flex-1 w-[612px] rounded-t-lg object-cover group-hover:brightness-[0.8] mb-3',
      headline:
        'w-full text-ellipsis group-hover:underline !font-bold !text-2xl line-clamp-2 text-content-primary mb-3 px-4 order-2',
      publisher: {
        wrapper: 'flex items-center gap-1 mb-3 px-4 order-1',
        text: 'text-xs text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 h-[40px] max-w-[443px] order-3',
      button: 'min-w-[97px] h-[40px] rounded-lg order-4',
    }
  }

  if (props.design === 'msn' && props.variant === '2x2-top-headline') {
    return {
      wrapper:
        'h-[620px] w-[612px] flex-col group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      headline:
        'h-[64px] w-full text-ellipsis group-hover:underline !font-bold !text-2xl line-clamp-2 text-content-primary mb-4 mt-4 px-4',
      image:
        'h-[422px] w-full aspect-[3/2] object-cover group-hover:brightness-[0.8] mb-3',
      publisher: {
        wrapper: 'flex items-center gap-1 mb-1 mt-2 px-4',
        text: 'text-xs text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 h-[40px] max-w-[443px] mb-3',
      button: 'min-w-[97px] h-[40px] rounded-lg',
    }
  }

  // SLIDER STYLES
  if (
    props.design === 'google' &&
    props.variant === 'default' &&
    props.size === 'large'
  ) {
    return {
      wrapper:
        'h-[304px] w-[612px] flex-col p-4 group flex bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'h-[180px] w-[580px] rounded object-cover group-hover:brightness-[0.8] mb-4',
      headline:
        'h-[48px] w-[580px] text-ellipsis group-hover:underline font-semibold text-base line-clamp-2 text-content-primary text-[40px] mb-2',
      playButton: 'h-12',
      publisher: {
        wrapper: 'flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  if (
    props.design === 'msn' &&
    props.variant === 'transparent' &&
    props.size === 'large'
  ) {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[304px] w-[612px] rounded-lg object-cover group-hover:brightness-[0.8]',
      headline:
        'h-[56px] text-ellipsis !font-bold text-base line-clamp-2 text-white !text-[20px] px-4 !leading-[28px] relative bottom-[104px]',
      playButton: 'h-32',
      publisher: {
        wrapper: 'flex items-center gap-1 px-4 relative bottom-[184px]',
        text: '!text-xs text-white font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
        ad:
          !!props.ad &&
          '!text-xs text-white font-normal !leading-[16px] bottom-[100px] px-4 relative',
      },
    }
  }

  // ARTICLE GROUP STYLES
  if (
    props.design === 'google' &&
    props.variant === 'default' &&
    props.size === 'tiny'
  ) {
    return {
      wrapper:
        'h-[88px] w-[300px] px-4 py-3 grid grid-rows-3 grid-cols-[minmax(188px,_1fr)_64px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md',
      image:
        'h-[64px] w-[64px] rounded object-cover group-hover:brightness-[0.8] row-span-3 col-start-2',
      headline:
        'text-ellipsis group-hover:underline !font-medium text-base line-clamp-2 text-content-primary text-sm row-start-1 row-end-3 col-start-1 col-span-1 max-w-[172px]',
      publisher: {
        wrapper: 'flex items-center gap-1 col-span-1 col-start-1 row-start-3',
        text: 'text-sm text-content-secondary font-normal line-clamp-1',
        icon:
          !!props.publisherIconUrl &&
          !props.ad &&
          'h-[16px] w-[16px] rounded-full',
        ad:
          !!props.publisherName &&
          !!props.ad &&
          'text-sm text-content-secondary font-normal grow text-right',
      },
    }
  }
  if (
    props.design === 'msn' &&
    props.variant === 'default' &&
    props.size === 'tiny'
  ) {
    return {
      wrapper:
        'h-[76px] w-[284px] bg-background-default hover:cursor-pointer flex flex-col gap-4px rounded-6px hover:bg-action-neutral-onSubtle-hover p-8px',
      headline:
        'text-ellipsis !font-semibold text-base line-clamp-2 text-content-primary !text-sm !leading-[20px] order-2',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // YOLO-L STYLES
  if (props.design === 'google' && props.variant === 'yolo-l') {
    return {
      wrapper:
        'h-[304px] w-[612px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  if (props.design === 'msn' && props.variant === 'yolo-l') {
    return {
      wrapper:
        'h-[304px] w-[612px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[300px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  // YOLO-XL STYLES
  if (props.design === 'google' && props.variant === 'yolo-xl') {
    return {
      wrapper:
        'h-[304px] w-[924px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-2 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  if (props.design === 'msn' && props.variant === 'yolo-xl') {
    return {
      wrapper:
        'h-[304px] w-[924px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[300px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  // YOLO-XXL STYLES
  if (props.design === 'google' && props.variant === 'yolo-xxl') {
    return {
      wrapper:
        'h-[304px] w-[1236px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold text-base line-clamp-2 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  if (props.design === 'msn' && props.variant === 'yolo-xxl') {
    return {
      wrapper:
        'h-[304px] w-[1236px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[612px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[612px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[24px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-sm text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-xs text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  // YOLO-FULL STYLES
  if (props.design === 'msn' && props.variant === 'yolo-full') {
    return {
      wrapper:
        'h-[304px] w-[1548px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[612px,_1fr] gap-x-7 pr-[24px]',
      image:
        'h-[304px] w-[798px] rounded-l-lg object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis !font-bold line-clamp-3 text-content-primary !text-[28px] col-start-2 !leading-8 mb-12px order-2',
      subtitle:
        'text-base text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px order-3',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1 order-1 mb-3',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
      button: 'w-fit !self-end order-4',
    }
  }

  if (props.design === 'google' && props.variant === 'yolo-full') {
    return {
      wrapper:
        'h-[304px] w-[1548px] p-4 group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-12px hover:shadow-md grid grid-cols-[300px,_1fr] gap-x-7',
      image:
        'h-[272px] w-[300px] rounded object-cover group-hover:brightness-[0.8] col-start-1',
      headline:
        'text-ellipsis group-hover:underline !font-bold line-clamp-2 text-content-primary !text-[28px] col-start-2 !leading-8 mb-12px',
      subtitle:
        'text-base self-start text-content-secondary font-normal text-ellipsis line-clamp-2 mb-24px',
      publisher: {
        wrapper: 'h-[16px] flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // TRANSPARENT - DEFAULT
  if (
    props.design === 'msn' &&
    props.variant === 'transparent' &&
    props.size === 'default'
  ) {
    return {
      wrapper:
        'h-[304px] w-[300px] group bg-background-default hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[304px] w-[300px] rounded-lg object-cover group-hover:brightness-[0.8]',
      headline:
        'text-ellipsis  !font-bold text-base line-clamp-3 text-content-inverted-primary !text-[20px] px-4 !leading-[28px] relative bottom-[116px]',
      publisher: {
        wrapper: 'flex items-center gap-1 px-4 relative bottom-[216px]',
        text: '!text-xs text-content-inverted-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  if (props.design === 'msn' && props.variant === 'title-msn') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[158px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
      headline:
        'h-[84px] text-ellipsis !font-bold text-base line-clamp-3 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
      publisher: {
        wrapper: 'flex items-center gap-1 order-2 px-4',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }

  // TITLE STYLES - DEFAULT
  if (props.design === 'google') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col p-4 group flex bg-background-default hover:cursor-pointer shadow-article-card rounded-12px hover:shadow-md',
      image: 'h-[180px] rounded object-cover group-hover:brightness-[0.8] mb-4',
      headline:
        'text-ellipsis group-hover:underline font-semibold text-base line-clamp-2 text-content-primary text-[40px] mb-2',
      publisher: {
        wrapper: 'flex items-center gap-1',
        text: 'text-sm text-content-secondary font-normal',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  if (props.design === 'msn') {
    return {
      wrapper:
        'h-[304px] w-[300px] flex-col group flex bg-background-default transition duration-300 hover:cursor-pointer border-article-card-border shadow-article-card rounded-8px hover:shadow-md',
      image:
        'h-[202px] rounded-t-lg object-cover transition duration-300 group-hover:brightness-[0.8] order-1 mb-12px',
      headline:
        'text-ellipsis !font-bold text-base line-clamp-2 text-content-primary !text-[20px] order-3 px-4 !leading-[28px]',
      publisher: {
        wrapper: 'flex items-center gap-1 order-2 px-4 mb-[2px]',
        text: '!text-xs text-content-secondary font-normal !leading-[16px]',
        icon: !!props.publisherIconUrl && 'h-[16px] w-[16px] rounded-full',
      },
    }
  }
  return {}
}
